import React, { useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

/**
 * Thin Chart.js wrapper — the single place a canvas is created in the app.
 *
 * The instance is created once and then updated in place (rather than being
 * torn down on every render) so the canvas keeps the size it was laid out
 * with; recreating it on each data change makes it shrink inside flex parents.
 *
 * Props
 *  - type     Chart.js type: "bar" | "line" | "doughnut" | "pie" | …
 *  - data     Chart.js data object
 *  - options  Chart.js options object
 *  - plugins  inline plugins for this chart only
 *  - ariaLabel accessible description of what the chart shows
 */
export default function ChartCanvas({
  type = "bar",
  data,
  options,
  plugins = [],
  ariaLabel,
  className,
}) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  // Keep the latest data/options readable from the create-effect without
  // making it re-run (and destroy the canvas) whenever they change.
  const latest = useRef({ data, options, plugins });
  latest.current = { data, options, plugins };

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return undefined;

    const { data: d, options: o, plugins: p } = latest.current;
    chartRef.current = new Chart(el, { type, data: d, options: o, plugins: p });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  }, [type]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    chart.config.data = data;
    chart.config.options = options;
    chart.update();
  }, [data, options]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      role="img"
      aria-label={ariaLabel}
    />
  );
}
